import { createContext, type PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient, responseData } from "@/api/client";
import type { Hotel } from "@/api/types";

interface WorkspaceValue {
  hotels: Hotel[]; hotel?: Hotel; hotelId: string; period: string; isLoading: boolean; error: Error | null;
  retry(): void; setHotelId(id: string): void; setPeriod(period: string): void;
}
const WorkspaceContext = createContext<WorkspaceValue | null>(null);
export function WorkspaceProvider({ children }: PropsWithChildren) {
  const hotelsQuery = useQuery({
    queryKey: ["hotels"],
    queryFn: () =>
      responseData(apiClient.GET("/api/v1/hotels", { params: { query: { page: 1, pageSize: 100, active: "true" } } })),
    retry: 1
  });
  const hotels = hotelsQuery.data?.items ?? [];
  const [hotelId, setHotelIdState] = useState(() => localStorage.getItem("hotelId") ?? "");
  const [period, setPeriodState] = useState(() => localStorage.getItem("period") ?? new Date().toISOString().slice(0, 7));
  useEffect(() => { if (hotelId && hotels.length && !hotels.some((item) => item.id === hotelId)) { localStorage.removeItem("hotelId"); setHotelIdState(""); } }, [hotelId, hotels]);
  const setHotelId = (id: string) => { localStorage.setItem("hotelId", id); setHotelIdState(id); };
  const setPeriod = (value: string) => { localStorage.setItem("period", value); setPeriodState(value); };
  const value = useMemo(() => ({ hotels, hotel: hotels.find((item) => item.id === hotelId), hotelId, period, isLoading: hotelsQuery.isLoading, error: hotelsQuery.error instanceof Error ? hotelsQuery.error : null, retry: () => void hotelsQuery.refetch(), setHotelId, setPeriod }), [hotels, hotelId, period, hotelsQuery.isLoading, hotelsQuery.error, hotelsQuery.refetch]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
export function useWorkspace() { const value = useContext(WorkspaceContext); if (!value) throw new Error("WorkspaceProvider no está configurado."); return value; }
